import {
    Box,
    FormControl,
    FormLabel,
    Input,
    FormHelperText,
    InputGroup,
    InputRightElement,
    IconButton,
    useToast,
    Flex,
    Button,
} from '@chakra-ui/react';
import { FaCopy } from 'react-icons/fa';
import { StorageProvider } from './StorageProviderSelector';

interface CloudStorageURLInputProps {
    provider: StorageProvider;
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    isDisabled?: boolean;
}

export const CloudStorageURLInput = ({
    provider,
    value,
    onChange,
    onSubmit,
    isDisabled = false,
}: CloudStorageURLInputProps) => {
    const toast = useToast();

    const getPlaceholder = () => {
        switch (provider) {
            case 'azure':
                return 'https://youraccountstorage.blob.core.windows.net/container/file?sv=...';
            case 'aws':
                return 'https://bucket-name.s3.region.amazonaws.com/file?...';
            case 'gcp':
                return 'https://storage.googleapis.com/bucket-name/file?...';
            default:
                return '';
        }
    };

    const getLabel = () => {
        switch (provider) {
            case 'azure':
                return 'Azure ADLS SAS Token URL';
            case 'aws':
                return 'AWS S3 Presigned URL';
            case 'gcp':
                return 'Google Cloud Storage Signed URL';
            default:
                return 'Cloud Storage URL';
        }
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            onChange(text);
            toast({
                title: 'URL pasted',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Failed to paste',
                description: 'Please manually paste the URL',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const isValidURL = value.trim().startsWith('http://') || value.trim().startsWith('https://');

    return (
        <Box maxW="800px" mx="auto" mt={6}>
            <FormControl>
                <FormLabel fontSize="lg" fontWeight="semibold" color="primary.500">
                    {getLabel()}
                </FormLabel>
                <InputGroup size="lg">
                    <Input
                        placeholder={getPlaceholder()}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        borderColor="surface.300"
                        _hover={{ borderColor: 'primary.500' }}
                        _focus={{ borderColor: 'primary.500', boxShadow: '0 0 0 1px #1a1262' }}
                        isDisabled={isDisabled}
                    />
                    <InputRightElement>
                        <IconButton
                            aria-label="Paste URL"
                            icon={<FaCopy />}
                            size="sm"
                            onClick={handlePaste}
                            variant="ghost"
                            color="muted.400"
                            _hover={{ color: 'primary.500' }}
                            isDisabled={isDisabled}
                        />
                    </InputRightElement>
                </InputGroup>
                <FormHelperText>
                    Paste the presigned/signed URL from your cloud storage provider
                </FormHelperText>
            </FormControl>

            {/* Submit Button */}
            <Flex justify="center" mt={6}>
                <Button
                    bg="primary.500"
                    fontSize="xl"
                    color="white"
                    fontWeight="bold"
                    px={12}
                    py={6}
                    borderRadius="full"
                    onClick={onSubmit}
                    isDisabled={!isValidURL || isDisabled}
                    _hover={{ bg: '#23194e', transform: 'scale(1.03)' }}
                    _active={{ bg: '#1b133f', transform: 'scale(0.99)' }}
                    transition="all 0.18s ease-in-out"
                >
                    Continue with this URL
                </Button>
            </Flex>
        </Box>
    );
};
